import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  InputAdornment, IconButton, CircularProgress, alpha, FormControlLabel, Checkbox
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Person, ArrowForward, PhoneAndroid, Email } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const Login = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState('');

  // OTP state
  const [otpStep, setOtpStep] = useState(false);
  const [otpMethod, setOtpMethod] = useState('email');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [canRemember, setCanRemember] = useState(false);

  // Check for session expired message on mount
  React.useEffect(() => {
    const msg = localStorage.getItem('sessionExpiredMsg');
    if (msg) {
      setInfoMsg(msg);
      localStorage.removeItem('sessionExpiredMsg');
    }
  }, []);

  // Get or create a persistent device ID for trusted device tracking
  const getDeviceId = () => {
    let id = localStorage.getItem('deviceId');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('deviceId', id);
    }
    return id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { setError('Please fill in all fields'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.login({ username: username.trim(), password, deviceId: getDeviceId() });
      if (res.data.requireOtp) {
        // 2FA required
        setOtpStep(true);
        setOtpMethod(res.data.method || 'email');
        setOtpMessage(res.data.message || '');
        setMaskedEmail(res.data.email || '');
        setCanRemember(!!res.data.canRemember);
        setRememberMe(false);
        setOtpCode('');
      } else if (res.data.token) {
        // Direct login (no 2FA)
        loginWithToken(res.data.token, res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) { setError('Please enter the verification code'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.verifyOtp({ username: username.trim(), otp: otpCode.trim(), method: otpMethod, rememberMe, deviceId: getDeviceId() });
      if (res.data.token) {
        loginWithToken(res.data.token, res.data.user);
        if (res.data.needs2FASetup) {
          navigate('/security');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code.');
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      const res = await authAPI.resendOtp({ username: username.trim() });
      setOtpMessage(res.data.message || 'A new code has been sent.');
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code.');
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpCode('');
    setOtpMessage('');
    setError('');
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Decorative elements */}
      <Box sx={{
        position: 'absolute', width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
        top: -200, right: -100
      }} />
      <Box sx={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
        bottom: -150, left: -100
      }} />

      <Card sx={{
        width: '100%', maxWidth: 420, mx: 2, borderRadius: 4, position: 'relative', zIndex: 1,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          {/* Logo / Branding */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(59,130,246,0.3)'
            }}>
              <Typography sx={{ fontWeight: 900, fontSize: 24, color: '#fff' }}>M</Typography>
            </Box>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
              MyPayPlus
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>
              Healthcare Billing Management
            </Typography>
          </Box>

          {infoMsg && (
            <Alert severity="warning" sx={{
              mb: 2, borderRadius: 2.5, fontSize: '0.82rem',
              bgcolor: alpha('#f59e0b', 0.08), border: `1px solid ${alpha('#f59e0b', 0.2)}`,
              '& .MuiAlert-icon': { color: '#f59e0b' }
            }}>
              {infoMsg}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{
              mb: 3, borderRadius: 2.5, fontSize: '0.82rem',
              bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.2)}`,
              '& .MuiAlert-icon': { color: '#ef4444' }
            }}>
              {error}
            </Alert>
          )}

          {!otpStep ? (
            /* ═══ Login Form ═══ */
            <form onSubmit={handleSubmit}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                Username
              </Typography>
              <TextField
                fullWidth value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username" autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ fontSize: 20, color: '#94a3b8' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2.5 }}
              />

              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                Password
              </Typography>
              <TextField
                fullWidth type={showPw ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ fontSize: 20, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPw(!showPw)} edge="end" sx={{ color: '#94a3b8' }}>
                        {showPw ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3.5 }}
              />

              <Button
                fullWidth type="submit" variant="contained" size="large" disabled={loading}
                endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                sx={{
                  py: 1.5, fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.02em',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    boxShadow: '0 6px 20px rgba(59,130,246,0.45)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            /* ═══ OTP Verification Form ═══ */
            <form onSubmit={handleVerifyOtp}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
                  bgcolor: alpha(otpMethod === 'totp' ? '#10b981' : '#3b82f6', 0.1),
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {otpMethod === 'totp' ? (
                    <PhoneAndroid sx={{ fontSize: 28, color: '#10b981' }} />
                  ) : (
                    <Email sx={{ fontSize: 28, color: '#3b82f6' }} />
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>
                  {otpMethod === 'totp' ? 'Authenticator Verification' : 'Email Verification'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {otpMethod === 'totp'
                    ? 'Enter the 6-digit code from your authenticator app'
                    : `Enter the verification code sent to ${maskedEmail}`
                  }
                </Typography>
              </Box>

              <TextField
                fullWidth value={otpCode} autoFocus
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: { letterSpacing: 12, textAlign: 'center', fontSize: '1.5rem', fontWeight: 700 }
                }}
                sx={{ mb: canRemember ? 1 : 3 }}
              />

              {canRemember && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      size="small"
                      sx={{ color: '#64748b', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>Remember this device</Typography>}
                  sx={{ mb: 2, ml: 0 }}
                />
              )}

              <Button
                fullWidth type="submit" variant="contained" size="large" disabled={loading || otpCode.length < 6}
                endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                sx={{
                  py: 1.5, fontWeight: 700, fontSize: '0.9rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669, #047857)',
                    boxShadow: '0 6px 20px rgba(16,185,129,0.45)',
                  }
                }}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button size="small" onClick={handleBackToLogin} sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Back to Login
                </Button>
                {otpMethod === 'email' && (
                  <Button size="small" onClick={handleResendOtp} sx={{ fontSize: '0.8rem', color: '#3b82f6' }}>
                    Resend Code
                  </Button>
                )}
              </Box>
            </form>
          )}

          {!otpStep && (
            <>
              <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '0.82rem', color: '#64748b' }}>
                New business?{' '}
                <Typography component="span" onClick={() => navigate('/request-access')}
                  sx={{ color: '#10b981', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                  Request Access
                </Typography>
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
